import { Spin, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React from 'react';
import { statusApi } from '../../../api';
import constants from '../../../constants';
import { buildColumn, getDefaultTablePaginationConfig, ScrollType } from '../../../Helper';
import { WidgetInfo, XboxStatusViewer } from '../../../model/StatusModel';
import './xboxStatus.scss';

// Internal state for the component
interface State {
	isLoading: boolean;
	data: XboxStatusViewer[];
}

// Normal properties for the component
interface OwnProps {
	isAll?: boolean;
	isExpanded: boolean;
	widgetInfo: WidgetInfo;
}

// combine them together
type Props = OwnProps;

//Avoid creating object style inline, since increases reconciliations
const scroll: ScrollType = { x: 1500, y: 312 };
const pagination: TablePaginationConfig = getDefaultTablePaginationConfig();
const columns: ColumnsType<any> = [
	{
		...buildColumn(_('XboxName'), 'XboxName', '16%', 'left'),
		sorter: (a, b) => a.XboxName.localeCompare(b.XboxName),
	},
	{
		...buildColumn(_('XboxAddress'), 'XBoxAddress', '16%', 'left'),
		sorter: (a, b) => a.XBoxAddress.localeCompare(b.XBoxAddress),
	},
	{
		...buildColumn(_('ControllerName'), 'ControllerName', '15%', 'left'),
		sorter: (a, b) => a.ControllerName.localeCompare(b.ControllerName),
	},
	{
		...buildColumn(_('ControllerAddress'), 'ControllerAddress', '10%', 'left'),
		sorter: (a, b) => a.ControllerAddress.localeCompare(b.ControllerAddress),
	},
	{
		...buildColumn(_('Status'), 'Status', '7%', 'left'),
		sorter: (a, b) => (a.Status ? a.Status.localeCompare(b.Status) : ''),
	},
	{
		...buildColumn(_('Enabled'), 'Enabled', '7%', 'left'),
		sorter: (a, b) => (a.Enable ? a.Enable.localeCompare(b.Enable) : ''),
	},
];

class XboxStatus extends React.Component<Props, State> {
	constructor(prop: Props) {
		super(prop);

		this.state = {
			isLoading: true,
			data: null,
		};

		//We use this flag to avoid multiple api calls at the same time
		window.sessionStorage.setItem(constants.sessionStorage.statusViewer.RETRIEVE_XBOX_STATUS_FLAG, '0');
	}

	componentDidMount() {
		this.fetchData();
	}

	componentWillUnmount() {
		this.clearFetchTimeout();
	}

	componentDidCatch(error, errorInfo) {
		console.log(error, errorInfo);
	}

	clearFetchTimeout() {
		if (this.autoRefreshPerformanceLoop) {
			clearTimeout(this.autoRefreshPerformanceLoop);
		}
	}

	private autoRefreshPerformanceLoop: null | ReturnType<typeof setTimeout> = null;

	shouldComponentUpdate(nextProps: Props, nextState: State) {
		const { isExpanded } = this.props;
		if (nextProps.isExpanded) {
			this.clearFetchTimeout();

			if (!isExpanded) {
				this.fetchData();
			} else {
				const { widgetInfo } = this.props;

				if (widgetInfo.autoRefresh) {
					this.autoRefreshPerformanceLoop = setTimeout(() => {
						this.fetchData();
					}, widgetInfo.autoRefreshTimer * 1000);
				}
			}
		}

		return JSON.stringify(this.state.data) !== JSON.stringify(nextState.data);
	}

	fetchData() {
		const sessionStorageKey = constants.sessionStorage.statusViewer.RETRIEVE_XBOX_STATUS_FLAG;
		if (window.sessionStorage.getItem(sessionStorageKey) === '0') {
			window.sessionStorage.setItem(sessionStorageKey, '1');

			statusApi
				.retrieveXboxStatusViewer()
				.then(response => {
					window.sessionStorage.setItem(sessionStorageKey, '0');

					this.setState({
						isLoading: false,
						data: response,
					});
				})
				.catch(e => console.log(e));
		}
	}

	render() {
		const { isLoading, data } = this.state;
		const { isAll } = this.props;

		let content;
		if (!isLoading) {
			let tableData;
			if (data && data.length) {
				tableData = data.map((xbox, index) => {
					return {
						key: index,
						XboxName: xbox.XboxName,
						XBoxAddress: xbox.XBoxAddress,
						ControllerName: xbox.ControllerName,
						ControllerAddress: xbox.ControllerAddress,
						Status: xbox.Status,
						Enabled: xbox.Enabled == 1 ? _('Yes') : _('No'),
					};
				});
			}

			content = (
				<Table
					id='statusDashboardStatusViewerXboxesTable'
					columns={columns}
					dataSource={tableData}
					scroll={scroll}
					size='small'
					pagination={pagination}
					className={'status-widget-table-nested'}
				/>
			);
		}

		return (
			<Spin tip={`${_('Loading')}...`} spinning={isLoading} size='default' className='spin-container'>
				<div className='xbox-status-container'>
					{isAll && (
						<div className='header'>
							<h3>{_('Xboxes')}</h3>
						</div>
					)}
					{content}
				</div>
			</Spin>
		);
	}
}

export { XboxStatus };
